// /client/src/components/RewardsView.js
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import RewardsContent from './RewardsContent';
import PageSEO from './PageSEO';

const RewardsView = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Comprehensive SEO configuration for rewards page
  const rewardsSEO = {
    title: "Kinder's Rewards Program | BBQ Restaurant Loyalty Program California",
    description: "Join Kinder's Rewards loyalty program and earn points on every BBQ purchase. Free fries or tots when you sign up! Redeem rewards at any of our 7 California locations. Sign up in seconds and start earning today.",
    keywords: [
      "Kinder's Rewards",
      "BBQ restaurant rewards program",
      "restaurant loyalty program California",
      "BBQ rewards",
      "East Bay restaurant rewards",
      "loyalty program benefits",
      "restaurant points program",
      "BBQ loyalty card",
      "California restaurant rewards",
      "dining rewards program",
      "food rewards",
      "restaurant membership program",
      "BBQ restaurant perks",
      "loyalty program signup",
      "restaurant customer rewards"
    ],
    canonicalUrl: "/rewards",
    ogImage: "https://kindersmeats.com/images/rewards-hero.webp",
    customMeta: {
      'application-name': "Kinder's Rewards Program",
      'apple-mobile-web-app-title': "Kinder's Rewards",
      'format-detection': 'telephone=no'
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LoyaltyProgram",
          "@id": "https://kindersmeats.com/rewards#loyaltyprogram",
          "name": "Kinder's Rewards",
          "description": "Join Kinder's Rewards loyalty program and earn points on every BBQ purchase. Free fries or tots when you sign up!",
          "url": "https://kindersmeats.com/rewards",
          "programMembershipUsed": "ProgramMembership",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Kinder's Rewards Benefits",
            "itemListElement": [
              {
                "@type": "Offer",
                "name": "Sign-up Bonus",
                "description": "Free fries or tots when you join",
                "category": "Welcome Bonus",
                "availability": "InStock"
              },
              {
                "@type": "Offer",
                "name": "Points on Purchases",
                "description": "Earn rewards points on every purchase",
                "category": "Ongoing Benefit"
              },
              {
                "@type": "Offer",
                "name": "Exclusive Member Offers",
                "description": "Special discounts and promotions for members",
                "category": "Member Exclusive"
              }
            ]
          },
          "provider": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "audience": {
            "@type": "Audience",
            "audienceType": "BBQ and restaurant customers",
            "geographicArea": {
              "@type": "State",
              "name": "California"
            }
          },
          "programBenefits": [
            "Earn points on every purchase",
            "Free fries or tots welcome bonus",
            "Exclusive member discounts",
            "Special promotions and offers",
            "Redeem rewards at any location"
          ],
          "applicationUrl": "https://kindersmeats.comosense.net/",
          "membershipPointsEarned": {
            "@type": "QuantitativeValue",
            "description": "Points earned per dollar spent",
            "unitText": "points per dollar"
          }
        },
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "url": "https://kindersmeats.com",
          "hasLoyaltyProgram": {
            "@id": "https://kindersmeats.com/rewards#loyaltyprogram"
          }
        },
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/rewards#webpage",
          "url": "https://kindersmeats.com/rewards",
          "name": "Kinder's Rewards Program | BBQ Restaurant Loyalty Program California",
          "description": "Join Kinder's Rewards loyalty program and earn points on every BBQ purchase. Free fries or tots when you sign up! Redeem rewards at any of our 7 California locations.",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/rewards#loyaltyprogram"
          },
          "mainEntity": {
            "@id": "https://kindersmeats.com/rewards#loyaltyprogram"
          },
          "breadcrumb": {
            "@id": "https://kindersmeats.com/rewards#breadcrumb"
          },
          "potentialAction": [
            {
              "@type": "JoinAction",
              "name": "Join Kinder's Rewards",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://kindersmeats.comosense.net/",
                "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
              }
            }
          ]
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/rewards#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Rewards Program",
              "item": "https://kindersmeats.com/rewards"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How do I join Kinder's Rewards?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Joining our loyalty program takes seconds! Simply visit our rewards signup page and create your account. You'll immediately receive your welcome bonus of free fries or tots."
              }
            },
            {
              "@type": "Question", 
              "name": "What do I get when I sign up?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "New members receive free fries or tots when they sign up for Kinder's Rewards program."
              }
            },
            {
              "@type": "Question",
              "name": "Where can I use my rewards?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can redeem your Kinder's Rewards at any of our 7 California locations in Brentwood, Concord, Hercules, Martinez, Pleasant Hill, and both Walnut Creek locations."
              }
            },
            {
              "@type": "Question",
              "name": "How do I earn points?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You earn rewards points on every purchase at any Kinder's location. Points can be redeemed for discounts and special offers on future visits."
              }
            }
          ]
        }
      ]
    }
  };

  return (
    <>
      {/* SEO Component */}
      <PageSEO {...rewardsSEO} />
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light section m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-start'>
            <Navigation 
              colorMode="colorWhite"
            />
            
            {/* Rewards Program Content */}
            <article itemScope itemType="https://schema.org/LoyaltyProgram">
              <RewardsContent 
                className="is-10-mobile is-10-tablet is-10-desktop is-10-widescreen is-10-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-1-desktop is-offset-1-widescreen is-offset-1-fullhd"
                title="Kinder's Rewards"
                subtitle="Rewarding the ultimate Barbecue Fans"
                body="Joining our loyalty program takes seconds, and you'll earn rewards to redeem on future visits."
                cta="Free Fries or Tots when you sign up!"
                buttonLabel="Join Kinder's Rewards"
                link={
                    <p className='subtitle colorBlack'>
                        Already enrolled? <Link to="https://kindersmeats.comosense.net/auth/signin" target="_blank" className="text-link-careers">Sign in here</Link>.
                    </p>
                }
                linkUrl="https://kindersmeats.comosense.net/"
                imageUrl="/images/rewards-hero.webp"
                colorMode="colorBlack"
              />
            </article>
            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default RewardsView;